import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { SessionReportResponse } from "../../interfaces/sessionReport";
import { useHttpClient } from "../../hooks/useHttpClient";
import { useLocalization } from "../../stores/localizationContext";
import { useUserContext } from "../../stores/userContext";
import SessionReportTable from "./SessionReportTable/SessionReportTable";
import "./SessionReport.scss";

const SessionReport: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t } = useLocalization();

  const [reportData, setReportData] = useState<SessionReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSessionReport = async () => {
      if (!user?.userId || !sessionId) return;

      setLoading(true);
      setError(null);

      try {
        const endpoint = import.meta.env.VITE_GET_SESSION_REPORT
          .replace("{educator_id}", user.userId)
          .replace("{session_id}", sessionId);

        const response = await api.get(endpoint);
        setReportData(response.data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError(t("unauthorized_access_error"));
        } else {
          setError(t("generic_error", "Something went wrong. Please try again later."));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSessionReport();
  }, [user?.userId, sessionId]);

  if (loading) {
    return (
      <div className="session-report__loading">
        <p dir="auto">{t("loading", "Loading...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="session-report__error">
        <p dir="auto">{error}</p>
      </div>
    );
  }

  if (!reportData) return null;

  return (
    <div className="session-report">
      <div className="container">
        <div className="session-report__meta">
          
          <p className="session-report__meta-item" dir="auto">
            <span className="session-report__meta-label">{t("subject_label", "Subject")}:</span>
            {" "}{reportData.subject}
          </p>
        </div>

        {reportData.session_report.length === 0 ? (
          <p className="session-report__empty" dir="auto">
            {t("no_report_data", "No report data available.")}
          </p>
        ) : (
          <SessionReportTable data={reportData} />
        )}
      </div>
    </div>
  );
};

export default SessionReport;
